/**
 * ContentMetadataTags customization for Portal IFPB.
 *
 * Important: this project uses the `@plonegovbr/volto-social-media` add-on, which
 * also customizes Volto's `ContentMetadataTags`.
 *
 * To remain future-proof, we *compose* the add-on implementation as the base
 * (so updates to the add-on are automatically inherited) and only add Portal
 * IFPB-specific tags on top:
 * - `og:site_name` and `og:type`
 * - fallback `og:image` when the content has no image
 * - full Twitter tags (`twitter:title`, `twitter:description`, `twitter:image`)
 */
import React from 'react';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { toPublicURL } from '@plone/volto/helpers/Url/Url';
import config from '@plone/volto/registry';

import SocialMediaContentMetadataTags from '@plonegovbr/volto-social-media/components/ComponentMetadataTags/ContentMetadataTags';

const DEFAULT_OG_IMAGE = '/og-image.png';

type ImageScale = {
  download: string;
  height?: number;
  width?: number;
};

type ImageField = {
  scales?: {
    large?: ImageScale;
  };
};

type ContentMetadataTagsContent = {
  '@id': string;
  '@type'?: string;
  title?: string;
  description?: string;
  opengraph_title?: string;
  opengraph_description?: string;
  opengraph_image?: ImageField;
  seo_title?: string;
  seo_description?: string;
  seo_canonical_url?: string;
  seo_noindex?: boolean;
  [key: string]: unknown;
};

type ContentMetadataTagsProps = {
  content: ContentMetadataTagsContent;
};

type ContentImageInfo = {
  contentHasImage: boolean;
  url: string | null;
  height: number | null;
  width: number | null;
};

const ContentMetadataTags = (props: ContentMetadataTagsProps) => {
  const {
    opengraph_title,
    opengraph_description,
    opengraph_image,
    seo_title,
    seo_description,
    title,
    description,
    '@type': contentType,
  } = props.content;

  const getContentImageInfo = (): ContentImageInfo => {
    const { contentMetadataTagsImageField } = config.settings as {
      contentMetadataTagsImageField?: string;
    };

    const imageFieldName = contentMetadataTagsImageField || 'image';
    const image = props.content[imageFieldName] as ImageField | undefined;

    const contentImageInfo: ContentImageInfo = {
      contentHasImage: false,
      url: null,
      height: null,
      width: null,
    };

    contentImageInfo.contentHasImage = Boolean(
      opengraph_image?.scales?.large?.download ||
        image?.scales?.large?.download,
    );

    if (contentImageInfo.contentHasImage && opengraph_image?.scales?.large) {
      contentImageInfo.url = opengraph_image.scales.large.download;
      contentImageInfo.height = opengraph_image.scales.large.height ?? null;
      contentImageInfo.width = opengraph_image.scales.large.width ?? null;
    } else if (contentImageInfo.contentHasImage && image?.scales?.large) {
      contentImageInfo.url = image.scales.large.download;
      contentImageInfo.height = image.scales.large.height ?? null;
      contentImageInfo.width = image.scales.large.width ?? null;
    }

    return contentImageInfo;
  };

  const contentImageInfo = getContentImageInfo();

  const siteTitleFromConfig = (config.settings as { siteTitle?: string })
    ?.siteTitle;
  const siteTitle = siteTitleFromConfig || 'Portal IFPB';

  const ogType = contentType === 'News Item' ? 'article' : 'website';

  const socialPreviewImageUrl =
    contentImageInfo.contentHasImage && contentImageInfo.url
      ? toPublicURL(contentImageInfo.url)
      : toPublicURL(DEFAULT_OG_IMAGE);

  return (
    <>
      {/* Base implementation (keeps add-on behavior and future updates). */}
      <SocialMediaContentMetadataTags {...props} />

      {/* Portal IFPB extensions. */}
      <Helmet>
        <meta property="og:site_name" content={siteTitle} />
        <meta property="og:type" content={ogType} />

        {/* Only add og:image when there is no content image.
            The base component already emits og:image when available. */}
        {!contentImageInfo.contentHasImage && (
          <meta property="og:image" content={socialPreviewImageUrl} />
        )}
        {!contentImageInfo.contentHasImage && (
          <meta property="og:image:width" content="1200" />
        )}
        {!contentImageInfo.contentHasImage && (
          <meta property="og:image:height" content="630" />
        )}

        {/* Twitter Card details.
            The base component emits twitter:card (and twitter:site if configured). */}
        <meta
          name="twitter:title"
          content={opengraph_title || seo_title || title}
        />
        {(opengraph_description || seo_description || description) && (
          <meta
            name="twitter:description"
            content={opengraph_description || seo_description || description}
          />
        )}
        <meta name="twitter:image" content={socialPreviewImageUrl} />
      </Helmet>
    </>
  );
};

export default ContentMetadataTags;
